import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import uploadTransport from '../../services/uploadTransport';
import type { TransferStatus } from '../../services/uploadTransport';

const retainedFiles = new Map<string, File>();
const cancelledQueuedIds = new Set<string>();
const submittedIds = new Set<string>();
const activeBatches = new Map<string,string[]>();
export const getRetainedUploadFile = (id: string) => retainedFiles.get(id);

export interface UploadItem {
  id: string;
  clientFileId?: string;
  name: string;
  size: number;
  type: 'file' | 'folder';
  progress: number;
  status: TransferStatus;
  error?: string;
  folderPath?: string;
  folderId?: string; // Target folder where file is being uploaded
  parentFolderId?: string | null; // Parent folder ID from backend response (for folder uploads)
  startTime?: number;
  endTime?: number;
  speed?: number; // bytes per second
  uploadResult?: {
    file?: any;
    folderPath?: string;
    parentFolderId?: string | null;
    action?: string;
  }; // Store upload result for refresh logic
}

export interface DialogUploadFile {
  id: string;
  name: string;
  size: number;
  status: string;
  progress: number;
}

export interface DialogUploadProgress {
  isActive: boolean;
  isChunked: boolean;
  progress: number;
  totalFiles: number;
  totalCompleted: number;
  totalFailed: number;
  totalUploading: number;
  totalPending: number;
  files: DialogUploadFile[];
}

export interface UploadState {
  uploads: UploadItem[];
  isUploading: boolean;
  totalProgress: number;
  showNotification: boolean;
  uploadQueue: string[]; // IDs of files waiting to upload
  activeUploads: string[]; // IDs of currently uploading files
  batchProgress: {
    currentBatch: number;
    totalBatches: number;
    currentBatchSize: number;
    processedInBatch: number;
  };
  dialogMinimized: boolean;
  dialogUploadProgress: DialogUploadProgress | null;
}

const initialState: UploadState = {
  uploads: [],
  isUploading: false,
  totalProgress: 0,
  showNotification: true,
  uploadQueue: [],
  activeUploads: [],
  batchProgress: {
    currentBatch: 0,
    totalBatches: 0,
    currentBatchSize: 0,
    processedInBatch: 0
  },
  dialogMinimized: false,
  dialogUploadProgress: null
};

interface UploadInput {clientFileId?: string; batchId?: string; file: File; folderId?: string; uploadId?: string; webkitRelativePath?: string | null; onProgress?: (progress: number) => void;}
const terminal = (status: string) => ['completed', 'error', 'cancelled'].includes(status);
const recalculate = (state: UploadState) => {
  let bytes=0,weighted=0,isUploading=false;
  const active:string[]=[],pending:string[]=[];
  for(const row of state.uploads){
    const status=row.status;
    if(!['cancelled','cancelling','error'].includes(status)){bytes+=row.size;weighted+=row.size*row.progress/100;}
    if(['uploading','processing','cancelling'].includes(status))active.push(row.id);
    if(status==='pending')pending.push(row.id);
    if(!terminal(status))isUploading=true;
  }
  state.totalProgress=bytes?Math.round(weighted/bytes*100):0;
  state.activeUploads=active;state.uploadQueue=pending;state.isUploading=isUploading;
};
const transfer = async (input: UploadInput, dispatch: any, registered = false) => {
  const id = input.uploadId || input.clientFileId || crypto.randomUUID();
  if(cancelledQueuedIds.has(id)) return {cancelled:true,uploadId:id};
  retainedFiles.set(id, input.file);
  submittedIds.add(id);
  if (!registered) dispatch(addUpload({id, clientFileId: input.clientFileId, name: input.file.name, size: input.file.size, type: 'file', progress: 0,
    status: 'pending', folderId: input.folderId, folderPath: input.webkitRelativePath || undefined, startTime: Date.now()}));
  let last = 0, lastStatus = '';
  try {
    const result = await uploadTransport.enqueue({id, batchId: input.batchId, file: input.file, folderId: input.folderId, path: input.webkitRelativePath || undefined,
      onProgress: progress => {
        const now = Date.now();
        if (now - last < 100 && progress.status === lastStatus && progress.progress < 100) return;
        last = now; lastStatus = progress.status;
        dispatch(updateUploadProgress({id, ...progress})); input.onProgress?.(progress.progress);
      }});
    dispatch(updateUploadProgress({id, status: 'completed', progress: 100, speed: 0, endTime: Date.now(), uploadResult: result, parentFolderId: result.parentFolderId}));
    retainedFiles.delete(id);
    return {...result, uploadId: id};
  } catch (error: any) {
    if (error.code === 'ERR_CANCELED') {retainedFiles.delete(id); return {cancelled: true, uploadId: id};}
    dispatch(updateUploadProgress({id, status: 'error', error: error.response?.data?.message || error.message, speed: 0}));
    throw error;
  } finally {submittedIds.delete(id);}
};
export const uploadFileBackground = createAsyncThunk('upload/file', (input: UploadInput, {dispatch}) => transfer(input, dispatch));
export const uploadFileWithFolderPath = createAsyncThunk('upload/fileWithPath', (input: UploadInput, {dispatch}) => transfer(input, dispatch));
export const uploadBatchBackground = createAsyncThunk('upload/batch', async (inputs: UploadInput[], {dispatch}) => {
  const batchId = crypto.randomUUID();
  const registered = inputs.map(input => ({...input, batchId: input.batchId || batchId, uploadId: input.uploadId || input.clientFileId || crypto.randomUUID()}));
  activeBatches.set(batchId,registered.map(input=>input.uploadId));
  for(let offset=0;offset<registered.length;offset+=512){
    dispatch(addUploads(registered.slice(offset,offset+512).map(input => ({id: input.uploadId, clientFileId: input.clientFileId,
      name: input.file.name, size: input.file.size, type: 'file', progress: 0, status: cancelledQueuedIds.has(input.uploadId)?'cancelled':'pending',
      folderId: input.folderId, folderPath: input.webkitRelativePath || undefined, startTime: Date.now()}))));
    if(registered.length>512)await new Promise(resolve=>setTimeout(resolve,0));
  }
  // Coalesce events across files, not just per file: avoid O(files × events) Immer scans.
  const changes=new Map<string, Partial<UploadItem> & {id:string}>();
  let timer:ReturnType<typeof setTimeout>|undefined;
  const flush=()=>{if(timer)clearTimeout(timer);timer=undefined;if(changes.size){dispatch(updateUploadsProgress(Array.from(changes.values())));changes.clear();}};
  const publish=(action:any)=>{
    if(action.type!==updateUploadProgress.type)return dispatch(action);
    const patch=action.payload;changes.set(patch.id,{...changes.get(patch.id),...patch});
    if(!timer)timer=setTimeout(flush,250);
  };
  let count=0,failed=0,cancelled=0;
  try {
    // Bounded intake as well as bounded network concurrency: never create 30,000 live promises.
    for(let offset=0;offset<registered.length;offset+=64){
      const results=await Promise.allSettled(registered.slice(offset,offset+64).map(input=>transfer(input,publish,true)));
      for(const result of results){if(result.status==='rejected')failed++;else if((result.value as any).cancelled)cancelled++;else count++;}
      await new Promise(resolve=>setTimeout(resolve,0));
    }
  } finally {flush();activeBatches.delete(batchId);for(const input of registered)cancelledQueuedIds.delete(input.uploadId);}
  return {count,failed,cancelled};
});
export const uploadFilesBackground = createAsyncThunk('upload/files', async ({files, folderId}: {files: File[]; folderId?: string}, {dispatch}) =>
  dispatch(uploadBatchBackground(files.map(file => ({file, folderId})))).unwrap());
export const uploadFolderBackground = createAsyncThunk('upload/folder', async ({files, folderId}: {files: {file: File; path: string}[]; folderId?: string}, {dispatch}) =>
  dispatch(uploadBatchBackground(files.map(({file,path}) => ({file, folderId, webkitRelativePath:path})))).unwrap());
const uploadSlice = createSlice({name: 'upload', initialState, reducers: {
  addUploads(state, action: PayloadAction<UploadItem[]>) {
    const incoming = new Map(action.payload.map(row => [row.id, row]));
    state.uploads = state.uploads.map(row => {const next = incoming.get(row.id); incoming.delete(row.id); return next || row;});
    state.uploads.push(...Array.from(incoming.values()));
    recalculate(state);
  },
  addUpload(state, action: PayloadAction<UploadItem>) {
    const index = state.uploads.findIndex(upload => upload.id === action.payload.id);
    if (index >= 0) state.uploads[index] = action.payload; else state.uploads.push(action.payload);
    recalculate(state);
  },
  updateUploadsProgress(state, action: PayloadAction<(Partial<UploadItem> & {id:string})[]>) {
    const patches=new Map(action.payload.map(patch=>[patch.id,patch]));
    for(const row of state.uploads){const patch=patches.get(row.id);if(patch && !(row.status==='cancelled' && patch.status!=='cancelled'))Object.assign(row,patch);}
    recalculate(state);
  },
  updateUploadProgress(state, action: PayloadAction<Partial<UploadItem> & {id: string}>) {
    const upload = state.uploads.find(row => row.id === action.payload.id);
    if (upload) Object.assign(upload, action.payload); recalculate(state);
  },
  discardUpload(state, action: PayloadAction<string>) {state.uploads = state.uploads.filter(row => row.id !== action.payload); recalculate(state);},
  clearCompletedUploads(state) {state.uploads = state.uploads.filter(row => row.status !== 'completed' && row.status !== 'cancelled'); recalculate(state);},
  hideCompletedUploads(state) {state.uploads = state.uploads.filter(row => !terminal(row.status)); recalculate(state);},
  toggleNotification(state) {state.showNotification = !state.showNotification;},
  setShowNotification(state, action: PayloadAction<boolean>) {state.showNotification = action.payload;},
  setBatchProgress(state, action: PayloadAction<UploadState['batchProgress']>) {state.batchProgress = action.payload;},
  updateBatchProgress(state, action: PayloadAction<{processedInBatch: number}>) {state.batchProgress.processedInBatch = action.payload.processedInBatch;},
  setDialogMinimized(state, action: PayloadAction<boolean>) {state.dialogMinimized = action.payload;},
  setDialogUploadProgress(state, action: PayloadAction<DialogUploadProgress | null>) {state.dialogUploadProgress = action.payload;},
  clearDialogUploadProgress(state) {state.dialogUploadProgress = null;}
}});
export const {addUpload, addUploads, updateUploadProgress, updateUploadsProgress, clearCompletedUploads, hideCompletedUploads, toggleNotification, setShowNotification,
  setBatchProgress, updateBatchProgress, setDialogMinimized, setDialogUploadProgress, clearDialogUploadProgress} = uploadSlice.actions;
export const pauseUpload = (id: string) => (dispatch: any) => {uploadTransport.pause(id);};
export const resumeUpload = (id: string) => (dispatch: any) => {dispatch(updateUploadProgress({id, status: 'pending'})); uploadTransport.resume(id);};
export const cancelUpload = (id: string) => (dispatch: any) => {cancelledQueuedIds.add(id);uploadTransport.cancel(id);if(!submittedIds.has(id))dispatch(updateUploadProgress({id,status:'cancelled',speed:0}));};
export const removeUpload = (id: string) => (dispatch: any, getState: any) => {
  const row = getState().upload.uploads.find((upload: UploadItem) => upload.id === id);
  if (row && !terminal(row.status)) dispatch(cancelUpload(id));
  else {retainedFiles.delete(id); dispatch(uploadSlice.actions.discardUpload(id));}
};
export const cancelAllUploads = () => (dispatch: any, getState: any) => {
  for(const ids of activeBatches.values())for(const id of ids)cancelledQueuedIds.add(id);
  const pending:any[]=[];
  for(const row of getState().upload.uploads as UploadItem[])if(!terminal(row.status)){
    cancelledQueuedIds.add(row.id);uploadTransport.cancel(row.id);
    if(!submittedIds.has(row.id))pending.push({id:row.id,status:'cancelled',speed:0});
  }
  if(pending.length)dispatch(updateUploadsProgress(pending));
};
export const retryUpload = (id: string) => (dispatch: any, getState: any) => {
  const row = getState().upload.uploads.find((upload: UploadItem) => upload.id === id);
  const file = retainedFiles.get(id);
  if (row?.status === 'error' && file) return dispatch(uploadFileWithFolderPath({file, folderId: row.folderId, webkitRelativePath: row.folderPath, uploadId: id}));
};
export const retryAllFailed = () => (dispatch: any, getState: any) => {getState().upload.uploads.filter((row: UploadItem) => row.status === 'error').forEach((row: UploadItem) => dispatch(retryUpload(row.id)));};

// Selectors
export const selectUploads = (state: RootState) => state.upload.uploads;
export const selectIsUploading = (state: RootState) => state.upload.isUploading;
export const selectTotalProgress = (state: RootState) => state.upload.totalProgress;
export const selectShowNotification = (state: RootState) => state.upload.showNotification;
export const selectActiveUploads = (state: RootState) => state.upload.activeUploads;
export const selectUploadQueue = (state: RootState) => state.upload.uploadQueue;
export const selectCompletedUploads = (state: RootState) => 
  state.upload.uploads.filter(u => u.status === 'completed');
export const selectFailedUploads = (state: RootState) => 
  state.upload.uploads.filter(u => u.status === 'error');
export const selectBatchProgress = (state: RootState) => state.upload.batchProgress;
export const selectDialogMinimized = (state: RootState) => state.upload.dialogMinimized;
export const selectDialogUploadProgress = (state: RootState) => state.upload.dialogUploadProgress;
export const selectIsAnyUploadActive = (state: RootState) =>
  state.upload.isUploading || (state.upload.dialogUploadProgress?.isActive === true);

export default uploadSlice.reducer; 